import React, { useEffect, useMemo, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Stage, Layer, Rect, Group, Text } from 'react-konva';

import ActionButton from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection';
import FloatingLayer from '../builder/FloatingLayer';
import SectionTitle from '../builder/SectionTitle';
import SectionSubtitle from '../builder/SectionSubtitle';
import { i18nIntl, isNotEmpty } from '../../lib/ep';
import { useImageLoader } from '../../lib/hooks';
import Spinner from '../builder/Spinner';

const white_color = '#FFFFFF';
const gray_color = '#000000';

const final_board_size = 800;	// px
const default_game_layout = {
	gap:20,
	padding:{
		top:20,
		right:20,
		bottom:20,
		left:20,
	},
};

const SmashItTemplateLayer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ full_loaded, setFullLoaded ] = useState(false);
	const [ filters_applied, setFiltersApplied ] = useState(false);
	const [ indexed_items, setIndexedItems ] = useState({});

	const size = useRef(final_board_size);
	const canvas_scale = 0.5;
	const refs = useRef([]);
	const stage = props?.stage ?? false;
	const board_grid_col = parseInt(stage?.smash_grid ?? 4);
	const board_grid_row = board_grid_col;

	const game_layout = stage?.smash_layout ?? default_game_layout;
	const grid_gap = parseInt(game_layout?.gap ?? default_game_layout.gap);
	const padding = { ...default_game_layout.padding, ...(game_layout?.padding ?? {}) };

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const calculateItemSize = () => {
		const size_w = (size.current - parseInt(padding.left) - parseInt(padding.right) - ((board_grid_col - 1) * grid_gap)) / board_grid_col;
		const size_h = (size.current - parseInt(padding.top) - parseInt(padding.bottom) - ((board_grid_row - 1) * grid_gap)) / board_grid_row;

		return { w:size_w, h:size_h };
	};

	const item_h_proportion = useRef(0.7);	// in % from 0-1 (cover_height_proportion equals to "1 - item_height_propotion")
	const item_size = useRef(calculateItemSize());

	const _width = size.current;
	const _height = size.current;

	// To avoid antialiasign strokes:
	const exact_item_w = Math.ceil(item_size.current.w);
	const exact_item_h = Math.ceil(item_size.current.h);

	const downloadForegroundView = () => {
		var link = document.getElementById('download_link');
		link.setAttribute('download', 'foreground_template.png');
		link.setAttribute('href', refs.current['smashit_fg_template_ref'].toDataURL({ pixelRatio:4 }).replace(/^data:image\/[^;]/, 'data:application/octet-stream'));
		link.click();
	};

	const prepareImagesToLoad = () => {
		const images = [];
		if(isNotEmpty(stage, 'smash_item_cover_image')) {
			images.push({
				id:'foreground',
				name:stage.smash_item_cover_image,
				url:stage.full_smash_item_cover_image,
				fit:'contain',
			});
		} else {
			images.push({
				id:'foreground',
				name:'foreground',
				url:'/tests/fg.png',
				image:'/tests/fg.png',
				fit:'contain',
			});
		}

		return images;
	};

	const images = useMemo(prepareImagesToLoad, [
		stage.smash_item_cover_image,
	]);

	const calcImageSize = (w, h, type = 'cover', use_full_size = false) => {
		const w_prop = (((!use_full_size) ? exact_item_w : size.current) + 1) / w;
		const h_prop = (((!use_full_size) ? exact_item_h : size.current) + 1) / h;

		if(type == 'contain') return Math.min(w_prop, h_prop);
		return Math.max(w_prop, h_prop);
	};

	const calcItemOffset = (item, _size) => {
		let offset_x = (_size * item.w - exact_item_w) / 2;
		let offset_y = (_size * item.h - exact_item_h) / 2;

		return { x:offset_x, y:offset_y };
	};

	const adaptImageSizes = (image) => {
		const _scale = calcImageSize(image.image_w, image.image_h, image.fit, image.use_full_size);
		const _offset = calcItemOffset({ w:image.image_w, h:image.image_h }, _scale);
	
		return { ...image, scale:_scale, offset:_offset };
	};

	useImageLoader(images, (loaded_images) => {
		const current = {};
		setFullLoaded(false);

		Object.keys(loaded_images).map((image_name) => {
			const image = loaded_images[image_name];

			if(!isNotEmpty(image, 'img')) return;

			const new_image = adaptImageSizes({
				fillPatternImage:image.img,
				fit:image.fit,
				image_w:image.w,
				image_h:image.h,
				use_full_size:image?.use_full_size ?? false,
			});

			const type = image.id;
			if(type == 'foreground') {
				item_h_proportion.current = image.h * exact_item_w / image.w;
			}

			current[type] = new_image;
		})

		setIndexedItems(current);
		setTimeout(() => {
			setFullLoaded(true);
		}, 0);
	});

	const clearCache = (col, row, target = 'item_fg') => {
		if(refs.current[`${target}_${col}_${row}`] == undefined) return;
		refs.current[`${target}_${col}_${row}`].clearCache();
	};

	const applyCache = (col, row, target = 'item_fg') => {
		if(refs.current[`${target}_${col}_${row}`] == undefined) return;
		refs.current[`${target}_${col}_${row}`].cache(default_cache_props);
	};

	const applyTint = (col, row, red, green, blue, target = 'item_fg') => {
		clearCache(col, row, target);
		applyCache(col, row, target);
		refs.current[`${target}_${col}_${row}`].filters([Konva.Filters.RGB]);
		refs.current[`${target}_${col}_${row}`].red(Math.min(255, Math.max(0, parseInt(red))));
		refs.current[`${target}_${col}_${row}`].green(Math.min(255, Math.max(0, parseInt(green))));
		refs.current[`${target}_${col}_${row}`].blue(Math.min(255, Math.max(0, parseInt(blue))));
		refs.current['layer'].draw();
	};

	const init = () => {
		[...Array(board_grid_col).keys()].map((item_x, col) => {
			[...Array(board_grid_row).keys()].map((item_y, row) => {
				applyTint(col, row, 0, 0, 0);
			});
		});

		setFiltersApplied(true);
	};

	useEffect(() => {
		setTimeout(() => {
			init();
		}, 100);
	}, []);

	if(!full_loaded) {
		return (
			<div class="canvas_cont">
				<Spinner>Loading game</Spinner>
			</div>
		)
	}

	const foreground_h = item_h_proportion.current;

	return (
		<FloatingLayer _class={{size:'auto'}} backButton={props.close}>
			<div>
				<SectionTitle>Template to design the foreground image</SectionTitle>
				<div class="roulette_slices_template" style={{ width:`calc(${canvas_scale * _width + 290}px + 1em)` }}>
					<div>
						<div>
							<SectionSubtitle>From this section, you can design the main foreground image for the game.</SectionSubtitle>

							<SectionSubtitle>In order to help you design the foreground image, we provide you a template in PNG format, which you can use as a guide with your image editing software to make sure that the design of the grid fits perfectly the space available in the game.</SectionSubtitle>

							<SectionSubtitle _class={{other:'gui_force_bold'}}>Important: We recommend downloading the template once you have configured the grid size, the item foreground image and layout design (gap and paddings) of the game, since in case of modifying the number any of those parameters, the image template will also change.</SectionSubtitle>

							<div class="img_specs">{final_board_size}×{final_board_size}px</div>
						</div>
					</div>

					<div class="img_template">
						<Stage ref={(node) => { refs.current['smashit_fg_template_ref'] = node; }} width={canvas_scale * _width} height={canvas_scale * _height}>
							<Layer
								ref={(node) => { refs.current[`layer`] = node; }}
								scaleX={canvas_scale}
								scaleY={canvas_scale}
							>
								<Rect
									width={_width}
									height={_height}
									fill={gray_color}
								/>

								{(full_loaded) ? (
									<Group
										x={parseInt(padding.left)}
										y={parseInt(padding.top)}
										opacity={(filters_applied) ? 1 : 0}
									>
										{[...Array(board_grid_col).keys()].map((item_x, col) => {
											return (
												[...Array(board_grid_row).keys()].map((item_y, row) => {
													return (
														<Group
															key={`active_whole_item_${col}_${row}`}
															x={col * (item_size.current.w + grid_gap)}
															y={row * (item_size.current.h + grid_gap)}
														>
															<Rect
																width={exact_item_w}
																height={exact_item_h}
																x={0}
																y={0}
																fill={white_color}
															/>

															<Group
																clipFunc={(context) => {
																	context.beginPath();
																	context.moveTo(0, 0);
																	context.lineTo(exact_item_w, 0);
																	context.lineTo(exact_item_w, foreground_h);
																	context.lineTo(0, foreground_h);
																	context.closePath();
																}}
																y={exact_item_h - foreground_h}
															>
																<Rect
																	ref={(node) => { refs.current[`item_fg_${col}_${row}`] = node; }}
																	width={exact_item_w}
																	height={foreground_h}
																	fillPatternImage={indexed_items.foreground.fillPatternImage}
																	fillPatternRepeat='no-repeat'
																	fillPatternScaleX={indexed_items.foreground.scale}
																	fillPatternScaleY={indexed_items.foreground.scale}
																	fillPatternOffsetX={0}
																	fillPatternOffsetY={0}
																	opacity={0.5}
																/>
															</Group>
														</Group>
													)
												})
											)
										})}
									</Group>
								) : (
									<Text
										text={i18n("Generating image")}
										fill={white_color}
										width={_width}
										height={_height}
										align='center'
										verticalAlign='middle'
										fontSize={20}
										fontStyle='bold'
										y={0}
									/>
								)}
							</Layer>
						</Stage>
					</div>
				</div>

				<ButtonCollection in_floating={true}>
					<a id="download_link" style={{display:'none'}} />

					<ActionButton
						icon='knk-download'
						onClick={() => downloadForegroundView()}
					>Download template</ActionButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

export default injectIntl(SmashItTemplateLayer);